import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const CreditCardUpgradeAnalyzer = () => {
  const content = article.find((item) => item.id === 7);
  const [currentCard, setCurrentCard] = useState({
    name: "Chase Freedom Unlimited®",
    issuer: "Chase",
    limit: "8000",
    apr: "18.99",
    rewardsRate: "1.5",
    annualFee: "0",
    cardType: "Cash Back",
    creditTier: "Standard",
    foreignFee: "3",
    insuranceBenefits: "Basic",
    loungeAccess: "No",
    signupBonus: "0",
    pointsValue: "1"
  });

  const [upgradeOptions, setUpgradeOptions] = useState([
    {
      id: 1,
      name: "Chase Sapphire Preferred®",
      issuer: "Chase",
      minScore: "670",
      annualFee: "95",
      rewardsRates: {
        travel: "5",
        dining: "3",
        groceries: "3",
        streaming: "3",
        others: "1"
      },
      signupBonus: "60000",
      pointsValue: "1.25",
      foreignFee: "0",
      insuranceBenefits: "Premium",
      loungeAccess: "No",
      creditTier: "Premium",
      upgradePath: "product_change",
      relationshipBonus: "10"
    },
    {
      id: 2,
      name: "Chase Sapphire Reserve®",
      issuer: "Chase",
      minScore: "720",
      annualFee: "550",
      rewardsRates: {
        travel: "10",
        dining: "3",
        groceries: "1",
        streaming: "1",
        others: "1"
      },
      signupBonus: "60000",
      pointsValue: "1.5",
      foreignFee: "0",
      insuranceBenefits: "Ultimate",
      loungeAccess: "Priority Pass",
      creditTier: "Premium Plus",
      upgradePath: "new_application",
      relationshipBonus: "15"
    }
  ]);

  const [financialProfile, setFinancialProfile] = useState({
    income: "6000",
    expenses: "4500",
    creditScore: "720",
    onTimePayments: "24",
    utilization: "25",
    openAccounts: "4",
    timeSinceLastUpgrade: "18",
    accountAge: "36",
    relationshipLength: "24",
    recentInquiries: "1",
    derogatoryMarks: "0"
  });

  const [spendingProfile, setSpendingProfile] = useState({
    groceries: "800",
    dining: "400",
    travel: "300",
    gas: "200",
    entertainment: "150",
    shopping: "300",
    utilities: "350",
    others: "200"
  });

  const [results, setResults] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  const [selectedUpgrade, setSelectedUpgrade] = useState(0);
  const [scenarioComparison, setScenarioComparison] = useState(null);
  const resultsRef = useRef();

  // Auto-calculate when inputs change
  useEffect(() => {
    const hasValidData = currentCard.limit && financialProfile.income;
    if (hasValidData) {
      setTimeout(() => {
        analyzeUpgrade();
      }, 100);
    }
  }, [currentCard, financialProfile, spendingProfile, upgradeOptions, selectedUpgrade]);

  const handleCurrentCardChange = (field, value) => {
    setCurrentCard({ ...currentCard, [field]: value });
  };

  const handleFinancialProfileChange = (field, value) => {
    setFinancialProfile({ ...financialProfile, [field]: value });
  };

  const handleSpendingChange = (category, value) => {
    setSpendingProfile({ ...spendingProfile, [category]: value });
  };

  const handleUpgradeOptionChange = (id, field, value, subField = null) => {
    const newOptions = upgradeOptions.map(option => {
      if (option.id === id) {
        if (subField) {
          return {
            ...option,
            [field]: {
              ...option[field],
              [subField]: value
            }
          };
        } else {
          return {
            ...option,
            [field]: value
          };
        }
      }
      return option;
    });
    setUpgradeOptions(newOptions);
  };

  const addUpgradeOption = () => {
    const newOption = {
      id: Date.now(),
      name: "New Upgrade Option",
      issuer: "Chase",
      minScore: "700",
      annualFee: "95",
      rewardsRates: {
        travel: "3",
        dining: "2",
        groceries: "2",
        streaming: "1",
        others: "1"
      },
      signupBonus: "50000",
      pointsValue: "1",
      foreignFee: "0",
      insuranceBenefits: "Standard",
      loungeAccess: "No",
      creditTier: "Premium",
      upgradePath: "product_change",
      relationshipBonus: "5"
    };
    setUpgradeOptions([...upgradeOptions, newOption]);
  };

  const removeUpgradeOption = (id) => {
    if (upgradeOptions.length > 1) {
      setUpgradeOptions(upgradeOptions.filter(option => option.id !== id));
      if (selectedUpgrade >= upgradeOptions.length - 1) {
        setSelectedUpgrade(0);
      }
    }
  };

  const resetCalculator = () => {
    setCurrentCard({
      name: "Chase Freedom Unlimited®",
      issuer: "Chase",
      limit: "8000",
      apr: "18.99",
      rewardsRate: "1.5",
      annualFee: "0",
      cardType: "Cash Back",
      creditTier: "Standard",
      foreignFee: "3",
      insuranceBenefits: "Basic",
      loungeAccess: "No",
      signupBonus: "0",
      pointsValue: "1"
    });
    setUpgradeOptions([
      {
        id: 1,
        name: "Chase Sapphire Preferred®",
        issuer: "Chase",
        minScore: "670",
        annualFee: "95",
        rewardsRates: {
          travel: "5",
          dining: "3",
          groceries: "3",
          streaming: "3",
          others: "1"
        },
        signupBonus: "60000",
        pointsValue: "1.25",
        foreignFee: "0",
        insuranceBenefits: "Premium",
        loungeAccess: "No",
        creditTier: "Premium",
        upgradePath: "product_change",
        relationshipBonus: "10"
      },
      {
        id: 2,
        name: "Chase Sapphire Reserve®",
        issuer: "Chase",
        minScore: "720",
        annualFee: "550",
        rewardsRates: {
          travel: "10",
          dining: "3",
          groceries: "1",
          streaming: "1",
          others: "1"
        },
        signupBonus: "60000",
        pointsValue: "1.5",
        foreignFee: "0",
        insuranceBenefits: "Ultimate",
        loungeAccess: "Priority Pass",
        creditTier: "Premium Plus",
        upgradePath: "new_application",
        relationshipBonus: "15"
      }
    ]);
    setFinancialProfile({
      income: "6000",
      expenses: "4500",
      creditScore: "720",
      onTimePayments: "24",
      utilization: "25",
      openAccounts: "4",
      timeSinceLastUpgrade: "18",
      accountAge: "36",
      relationshipLength: "24",
      recentInquiries: "1",
      derogatoryMarks: "0"
    });
    setSpendingProfile({
      groceries: "800",
      dining: "400",
      travel: "300",
      gas: "200",
      entertainment: "150",
      shopping: "300",
      utilities: "350",
      others: "200"
    });
    setResults(null);
    setSelectedUpgrade(0);
  };

  const analyzeUpgrade = () => {
    const profile = financialProfile;
    const current = currentCard;
    const upgradeOption = upgradeOptions[selectedUpgrade];
    const spending = spendingProfile;

    if (!current.limit || !profile.income || !upgradeOption) {
      alert("Please fill in required fields for current card, financial profile, and select an upgrade option");
      return;
    }

    // Calculate current rewards
    const totalMonthlySpending = Object.values(spending).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const currentRewards = (totalMonthlySpending * parseFloat(current.rewardsRate)) / 100;

    // Calculate upgrade rewards with category bonuses
    let upgradeRewards = 0;
    Object.entries(spending).forEach(([category, amount]) => {
      const rate = upgradeOption.rewardsRates[category] || upgradeOption.rewardsRates.others;
      upgradeRewards += ((parseFloat(amount) || 0) * parseFloat(rate)) / 100;
    });

    // Apply points multiplier for premium cards
    const pointsMultiplier = parseFloat(upgradeOption.pointsValue) || 1;
    const effectiveUpgradeRewards = upgradeRewards * pointsMultiplier;

    // Calculate signup bonus value (annualized first year)
    const signupBonusValue = (parseFloat(upgradeOption.signupBonus) || 0) * pointsMultiplier / 12;

    // Calculate net annual benefit
    const currentAnnualFee = parseFloat(current.annualFee) || 0;
    const upgradeAnnualFee = parseFloat(upgradeOption.annualFee) || 0;
    const monthlyRewardsIncrease = (effectiveUpgradeRewards - currentRewards) + signupBonusValue;
    const annualNetBenefit = (monthlyRewardsIncrease * 12) - (upgradeAnnualFee - currentAnnualFee);

    // Calculate approval score
    let approvalScore = calculateApprovalScore(profile, current, upgradeOption);
    const approvalLikelihood = getApprovalLikelihood(approvalScore);

    // Calculate estimated new limit
    const estimatedNewLimit = calculateNewLimit(current, profile, upgradeOption);

    // Calculate utilization impact
    const currentUtilization = parseFloat(profile.utilization) || 0;
    const currentBalance = (parseFloat(current.limit) * currentUtilization) / 100;
    const utilizationAfterUpgrade = (currentBalance / estimatedNewLimit) * 100;

    // Generate recommendations
    const recommendations = generateRecommendations(profile, current, upgradeOption, approvalScore);

    // Calculate break-even analysis
    const breakEvenMonths = upgradeAnnualFee > currentAnnualFee ? 
      Math.ceil((upgradeAnnualFee - currentAnnualFee) / (monthlyRewardsIncrease * 12)) : 0;

    // Calculate foreign fee savings
    const foreignSpending = (parseFloat(spending.travel) || 0) * 0.3; // Assume 30% foreign spending
    const foreignFeeSavings = (foreignSpending * (parseFloat(current.foreignFee) - parseFloat(upgradeOption.foreignFee))) / 100;

    // Calculate total first year value
    const firstYearValue = annualNetBenefit + (signupBonusValue * 12) + (foreignFeeSavings * 12);

    // Calculate scenario comparison
    calculateScenarioComparison(current, profile, spending, upgradeOptions);

    setResults({
      currentRewards: currentRewards.toFixed(2),
      upgradeRewards: effectiveUpgradeRewards.toFixed(2),
      monthlyRewardsIncrease: monthlyRewardsIncrease.toFixed(2),
      annualNetBenefit: annualNetBenefit.toFixed(2),
      firstYearValue: firstYearValue.toFixed(2),
      approvalScore,
      approvalLikelihood,
      estimatedNewLimit: estimatedNewLimit.toFixed(0),
      utilizationAfterUpgrade: utilizationAfterUpgrade.toFixed(2),
      breakEvenMonths,
      foreignFeeSavings: foreignFeeSavings.toFixed(2),
      signupBonusValue: (signupBonusValue * 12).toFixed(2),
      recommendations,
      upgradeOption: upgradeOption.name
    });
  };

  const calculateApprovalScore = (profile, current, upgrade) => {
    let score = 50;

    // Credit Score (25 points)
    const creditScore = parseInt(profile.creditScore) || 0;
    const minScore = parseInt(upgrade.minScore) || 0;
    if (creditScore >= minScore + 50) score += 25;
    else if (creditScore >= minScore + 25) score += 20;
    else if (creditScore >= minScore) score += 15;
    else if (creditScore >= minScore - 20) score += 5;
    else score -= 10;

    // Payment History (20 points)
    const onTimePayments = parseInt(profile.onTimePayments) || 0;
    if (onTimePayments >= 24) score += 20;
    else if (onTimePayments >= 12) score += 15;
    else if (onTimePayments >= 6) score += 10;
    else score += 5;

    // Utilization (15 points)
    const utilization = parseFloat(profile.utilization) || 0;
    if (utilization < 10) score += 15;
    else if (utilization < 30) score += 12;
    else if (utilization < 50) score += 8;
    else score -= 5;

    // Relationship Length (15 points)
    const relationshipLength = parseInt(profile.relationshipLength) || 0;
    if (relationshipLength >= 24) score += 15;
    else if (relationshipLength >= 12) score += 12;
    else if (relationshipLength >= 6) score += 8;
    else score += 5;

    // Time Since Last Upgrade (10 points)
    const timeSinceUpgrade = parseInt(profile.timeSinceLastUpgrade) || 0;
    if (timeSinceUpgrade >= 12) score += 10;
    else if (timeSinceUpgrade >= 6) score += 7;
    else if (timeSinceUpgrade >= 3) score += 3;
    else score -= 5;

    // Account Age (10 points)
    const accountAge = parseInt(profile.accountAge) || 0;
    if (accountAge >= 24) score += 10;
    else if (accountAge >= 12) score += 7;
    else if (accountAge >= 6) score += 3;
    else score += 1;

    // Upgrade Path Bonus (5 points)
    if (upgrade.upgradePath === "product_change") score += 5;

    return Math.max(0, Math.min(100, score));
  };

  const getApprovalLikelihood = (score) => {
    if (score >= 85) return "Very High";
    if (score >= 70) return "High";
    if (score >= 55) return "Good";
    if (score >= 40) return "Fair";
    return "Low";
  };

  const calculateNewLimit = (current, profile, upgrade) => {
    const currentLimit = parseFloat(current.limit) || 0;
    const income = parseFloat(profile.income) || 0;
    const creditScore = parseInt(profile.creditScore) || 0;
    
    let baseIncrease = currentLimit * 0.3; // 30% base increase
    let scoreMultiplier = creditScore / 800;
    let incomeMultiplier = Math.min(income * 0.1, currentLimit * 2); // Cap at 10x monthly income
    
    return currentLimit + (baseIncrease * scoreMultiplier);
  };

  const generateRecommendations = (profile, current, upgrade, approvalScore) => {
    const recommendations = [];

    if (approvalScore < 70) {
      recommendations.push({
        type: "warning",
        text: `Wait 3-6 months and improve credit score to ${parseInt(upgrade.minScore) + 20}+ for better approval chances`
      });
    }

    if (parseFloat(profile.utilization) > 30) {
      recommendations.push({
        type: "critical",
        text: "Pay down balances to reduce utilization below 30% before requesting upgrade"
      });
    }

    if (parseInt(profile.timeSinceLastUpgrade) < 6) {
      recommendations.push({
        type: "info",
        text: `Wait ${6 - parseInt(profile.timeSinceLastUpgrade)} more month(s) since last upgrade/application`
      });
    }

    if (upgrade.upgradePath === "new_application") {
      recommendations.push({
        type: "info",
        text: "This requires a new application which may result in a hard credit pull"
      });
    }

    if (parseFloat(upgrade.annualFee) > parseFloat(current.annualFee)) {
      recommendations.push({
        type: "info",
        text: `Ensure your spending patterns justify the $${(parseFloat(upgrade.annualFee) - parseFloat(current.annualFee)).toFixed(2)} annual fee increase`
      });
    }

    if (parseInt(profile.recentInquiries) > 0) {
      recommendations.push({
        type: "warning",
        text: `Consider waiting for ${6 - parseInt(profile.recentInquiries)} months for recent inquiries to age`
      });
    }

    return recommendations;
  };

  const calculateScenarioComparison = (current, profile, spending, options) => {
    const scenarios = options.map((option, index) => {
      const analysis = analyzeSingleUpgrade(current, profile, spending, option);
      return {
        name: option.name,
        annualNetBenefit: parseFloat(analysis.annualNetBenefit),
        approvalScore: analysis.approvalScore,
        firstYearValue: parseFloat(analysis.firstYearValue),
        breakEvenMonths: analysis.breakEvenMonths,
        annualFee: parseFloat(option.annualFee)
      };
    });

    setScenarioComparison(scenarios);
  };

  const analyzeSingleUpgrade = (current, profile, spending, upgrade) => {
    // Simplified version of the main analysis for scenario comparison
    const totalMonthlySpending = Object.values(spending).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const currentRewards = (totalMonthlySpending * parseFloat(current.rewardsRate)) / 100;
    
    let upgradeRewards = 0;
    Object.entries(spending).forEach(([category, amount]) => {
      const rate = upgrade.rewardsRates[category] || upgrade.rewardsRates.others;
      upgradeRewards += ((parseFloat(amount) || 0) * parseFloat(rate)) / 100;
    });

    const pointsMultiplier = parseFloat(upgrade.pointsValue) || 1;
    const effectiveUpgradeRewards = upgradeRewards * pointsMultiplier;
    const signupBonusValue = (parseFloat(upgrade.signupBonus) || 0) * pointsMultiplier / 12;

    const currentAnnualFee = parseFloat(current.annualFee) || 0;
    const upgradeAnnualFee = parseFloat(upgrade.annualFee) || 0;
    const monthlyRewardsIncrease = (effectiveUpgradeRewards - currentRewards) + signupBonusValue;
    const annualNetBenefit = (monthlyRewardsIncrease * 12) - (upgradeAnnualFee - currentAnnualFee);
    const firstYearValue = annualNetBenefit + (signupBonusValue * 12);

    const approvalScore = calculateApprovalScore(profile, current, upgrade);
    const breakEvenMonths = upgradeAnnualFee > currentAnnualFee ? 
      Math.ceil((upgradeAnnualFee - currentAnnualFee) / (monthlyRewardsIncrease * 12)) : 0;

    return {
      annualNetBenefit: annualNetBenefit.toFixed(2),
      approvalScore,
      firstYearValue: firstYearValue.toFixed(2),
      breakEvenMonths
    };
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  // Chart data configurations
  const rewardsComparisonData = results ? {
    labels: ['Current Card', 'Upgrade Option'],
    datasets: [
      {
        label: 'Monthly Rewards Value ($)',
        data: [
          parseFloat(results.currentRewards),
          parseFloat(results.upgradeRewards)
        ],
        backgroundColor: ['#A03B00', '#F06201'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const benefitsBreakdownData = results ? {
    labels: ['Rewards Increase', 'Sign-up Bonus', 'Fee Savings', 'Foreign Fee Savings'],
    datasets: [
      {
        label: 'Annual Value ($)',
        data: [
          parseFloat(results.monthlyRewardsIncrease) * 12,
          parseFloat(results.signupBonusValue),
          Math.max(0, parseFloat(currentCard.annualFee) - parseFloat(upgradeOptions[selectedUpgrade].annualFee)),
          parseFloat(results.foreignFeeSavings) * 12
        ],
        backgroundColor: ['#F06201', '#FF8C42', '#A03B00', '#FFB088'],
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  } : null;

  const approvalFactorsData = {
    labels: ['Credit Score', 'Payment History', 'Utilization', 'Relationship', 'Time Since Upgrade', 'Account Age'],
    datasets: [
      {
        label: 'Importance Weight',
        data: [25, 20, 15, 15, 10, 10],
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  };

  const scenarioComparisonData = scenarioComparison ? {
    labels: scenarioComparison.map(scenario => scenario.name),
    datasets: [
      {
        label: 'Annual Net Benefit ($)',
        data: scenarioComparison.map(scenario => scenario.annualNetBenefit),
        backgroundColor: scenarioComparison.map(scenario => 
          scenario.annualNetBenefit > 0 ? '#38a169' : '#e53e3e'
        ),
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Approval Score',
        data: scenarioComparison.map(scenario => scenario.approvalScore),
        backgroundColor: '#FF8C42',
        borderWidth: 2,
        borderColor: '#fff',
        type: 'line',
        yAxisID: 'y1'
      }
    ],
  } : null;

  const spendingDistributionData = {
    labels: Object.keys(spendingProfile),
    datasets: [
      {
        label: 'Monthly Spending ($)',
        data: Object.values(spendingProfile).map(val => parseFloat(val) || 0),
        backgroundColor: '#F06201',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  const scenarioChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Net Benefit ($)'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Approval Score'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const totalMonthlySpending = Object.values(spendingProfile).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Credit Card Upgrade Analyzer</h1>
          <p>Analyze upgrade opportunities and maximize your credit card benefits</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Current Card:</span>
              <strong>{currentCard.name}</strong>
            </div>
            <div className="summary-item">
              <span>Monthly Spending:</span>
              <strong>${totalMonthlySpending.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Upgrade Options:</span>
              <strong>{upgradeOptions.length}</strong>
            </div>
            {results && (
              <div className="summary-item">
                <span>Best Net Benefit:</span>
                <strong className={results.annualNetBenefit > 0 ? "savings" : "warning"}>
                  ${results.annualNetBenefit}/year
                </strong>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
          onClick={() => setActiveTab('calculator')}
        >
          <i className="fa-solid fa-calculator"></i>
          Calculator
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Calculator Inputs */}
        <div className={`left-panel ${activeTab === 'calculator' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Current Card Details</h2>
              <div className="card-actions">
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-info">
                <i className="fa-solid fa-lightbulb"></i>
                {" "}Analyze upgrade opportunities to maximize rewards and benefits while minimizing costs
              </div>
            </div>

            <div className="input-grid">
              <div className="input-group">
                <label>Card Name*</label>
                <input
                  type="text"
                  value={currentCard.name}
                  onChange={(e) => handleCurrentCardChange("name", e.target.value)}
                  placeholder="Chase Freedom Unlimited®"
                />
              </div>
              <div className="input-group">
                <label>Issuer*</label>
                <select
                  value={currentCard.issuer}
                  onChange={(e) => handleCurrentCardChange("issuer", e.target.value)}
                >
                  <option value="Chase">Chase</option>
                  <option value="American Express">American Express</option>
                  <option value="Bank of America">Bank of America</option>
                  <option value="Citi">Citi</option>
                  <option value="Capital One">Capital One</option>
                  <option value="Discover">Discover</option>
                </select>
              </div>
              <div className="input-group">
                <label>Credit Limit ($)*</label>
                <input
                  type="number"
                  value={currentCard.limit}
                  onChange={(e) => handleCurrentCardChange("limit", e.target.value)}
                  placeholder="8000"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label>APR (%)</label>
                <input
                  type="number"
                  value={currentCard.apr}
                  onChange={(e) => handleCurrentCardChange("apr", e.target.value)}
                  placeholder="18.99"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label>Base Rewards Rate (%)*</label>
                <input
                  type="number"
                  value={currentCard.rewardsRate}
                  onChange={(e) => handleCurrentCardChange("rewardsRate", e.target.value)}
                  placeholder="1.5"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label>Annual Fee ($)</label>
                <input
                  type="number"
                  value={currentCard.annualFee}
                  onChange={(e) => handleCurrentCardChange("annualFee", e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label>Foreign Transaction Fee (%)</label>
                <input
                  type="number"
                  value={currentCard.foreignFee}
                  onChange={(e) => handleCurrentCardChange("foreignFee", e.target.value)}
                  placeholder="3"
                  min="0"
                  max="10"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label>Card Type</label>
                <select
                  value={currentCard.cardType}
                  onChange={(e) => handleCurrentCardChange("cardType", e.target.value)}
                >
                  <option value="Cash Back">Cash Back</option>
                  <option value="Travel">Travel</option>
                  <option value="Points">Points</option>
                  <option value="Secured">Secured</option>
                  <option value="Business">Business</option>
                </select>
              </div>
            </div>
          </div>

          {/* Financial Profile */}
          <div className="payment-settings">
            <div className="section-header">
              <h2>Financial Profile</h2>
            </div>
            <div className="input-grid">
              <div className="input-group">
                <label>Monthly Income ($)*</label>
                <input
                  type="number"
                  value={financialProfile.income}
                  onChange={(e) => handleFinancialProfileChange("income", e.target.value)}
                  placeholder="6000"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label>Credit Score*</label>
                <input
                  type="number"
                  value={financialProfile.creditScore}
                  onChange={(e) => handleFinancialProfileChange("creditScore", e.target.value)}
                  placeholder="720"
                  min="300"
                  max="850"
                />
              </div>
              <div className="input-group">
                <label>Credit Utilization (%)*</label>
                <input
                  type="number"
                  value={financialProfile.utilization}
                  onChange={(e) => handleFinancialProfileChange("utilization", e.target.value)}
                  placeholder="25"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div className="input-group">
                <label>On-time Payments (Months)</label>
                <input
                  type="number"
                  value={financialProfile.onTimePayments}
                  onChange={(e) => handleFinancialProfileChange("onTimePayments", e.target.value)}
                  placeholder="24"
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>Account Age (Months)</label>
                <input
                  type="number"
                  value={financialProfile.accountAge}
                  onChange={(e) => handleFinancialProfileChange("accountAge", e.target.value)}
                  placeholder="36"
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>Relationship Length (Months)</label>
                <input
                  type="number"
                  value={financialProfile.relationshipLength}
                  onChange={(e) => handleFinancialProfileChange("relationshipLength", e.target.value)}
                  placeholder="24"
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>Time Since Last Upgrade (Months)</label>
                <input
                  type="number"
                  value={financialProfile.timeSinceLastUpgrade}
                  onChange={(e) => handleFinancialProfileChange("timeSinceLastUpgrade", e.target.value)}
                  placeholder="18"
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>Recent Inquiries (6 months)</label>
                <input
                  type="number"
                  value={financialProfile.recentInquiries}
                  onChange={(e) => handleFinancialProfileChange("recentInquiries", e.target.value)}
                  placeholder="1"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Spending Profile */}
          <div className="payment-settings">
            <div className="section-header">
              <h2>Monthly Spending Profile</h2>
            </div>
            <div className="input-grid">
              {Object.entries(spendingProfile).map(([category, amount]) => (
                <div key={category} className="input-group">
                  <label>{category.charAt(0).toUpperCase() + category.slice(1)} ($)</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => handleSpendingChange(category, e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Upgrade Options */}
          <div className="payment-settings">
            <div className="section-header">
              <h2>Upgrade Options</h2>
              <button className="btn-secondary" onClick={addUpgradeOption}>
                <i className="fa-solid fa-plus"></i> Add Option
              </button>
            </div>

            {/* Upgrade Option Navigation */}
            {upgradeOptions.length > 1 && (
              <div className="card-navigation">
                {upgradeOptions.map((option, index) => (
                  <button
                    key={option.id}
                    className={`nav-tab ${selectedUpgrade === index ? 'active' : ''}`}
                    onClick={() => setSelectedUpgrade(index)}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            )}

            {upgradeOptions.map((option, index) => (
              <div 
                key={option.id} 
                className={`card-input-group ${selectedUpgrade === index ? 'active' : ''}`}
              >
                <div className="card-header">
                  <h3>{option.name}</h3>
                  {upgradeOptions.length > 1 && (
                    <button 
                      className="btn-remove" 
                      onClick={() => removeUpgradeOption(option.id)}
                      title="Remove option"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="input-grid">
                  <div className="input-group">
                    <label>Card Name*</label>
                    <input
                      type="text"
                      value={option.name}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "name", e.target.value)}
                      placeholder="Chase Sapphire Preferred®"
                    />
                  </div>
                  <div className="input-group">
                    <label>Issuer*</label>
                    <select
                      value={option.issuer}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "issuer", e.target.value)}
                    >
                      <option value="Chase">Chase</option>
                      <option value="American Express">American Express</option>
                      <option value="Bank of America">Bank of America</option>
                      <option value="Citi">Citi</option>
                      <option value="Capital One">Capital One</option>
                      <option value="Discover">Discover</option>
                    </select>
                  </div>
                  <div className="input-group">
                    <label>Minimum Credit Score</label>
                    <input
                      type="number"
                      value={option.minScore}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "minScore", e.target.value)}
                      placeholder="670"
                      min="300"
                      max="850"
                    />
                  </div>
                  <div className="input-group">
                    <label>Annual Fee ($)*</label>
                    <input
                      type="number"
                      value={option.annualFee}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "annualFee", e.target.value)}
                      placeholder="95"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="input-group">
                    <label>Sign-up Bonus (Points)</label>
                    <input
                      type="number"
                      value={option.signupBonus}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "signupBonus", e.target.value)}
                      placeholder="60000"
                      min="0"
                    />
                  </div>
                  <div className="input-group">
                    <label>Points Value Multiplier</label>
                    <input
                      type="number"
                      value={option.pointsValue}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "pointsValue", e.target.value)}
                      placeholder="1.25"
                      min="1"
                      max="2"
                      step="0.01"
                    />
                  </div>
                  <div className="input-group">
                    <label>Foreign Transaction Fee (%)</label>
                    <input
                      type="number"
                      value={option.foreignFee}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "foreignFee", e.target.value)}
                      placeholder="0"
                      min="0"
                      max="10"
                      step="0.01"
                    />
                  </div>
                  <div className="input-group">
                    <label>Upgrade Path</label>
                    <select
                      value={option.upgradePath}
                      onChange={(e) => handleUpgradeOptionChange(option.id, "upgradePath", e.target.value)}
                    >
                      <option value="product_change">Product Change</option>
                      <option value="new_application">New Application</option>
                      <option value="targeted_offer">Targeted Offer</option>
                    </select>
                  </div>
                </div>

                {/* Rewards Rates */}
                <div className="cashback-section">
                  <h4>Rewards Rates (%)</h4>
                  <div className="input-grid">
                    {Object.entries(option.rewardsRates).map(([category, rate]) => (
                      <div key={category} className="input-group">
                        <label>{category.charAt(0).toUpperCase() + category.slice(1)} (%)</label>
                        <input
                          type="number"
                          value={rate}
                          onChange={(e) => handleUpgradeOptionChange(option.id, "rewardsRates", e.target.value, category)}
                          placeholder="1"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <div className="required-note">* Required fields</div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={analyzeUpgrade}>
              Analyze Upgrade
            </button>
          </div>

          {/* Default tips section */}
          <div className="default-results">
            <h2>Upgrade Strategy Guide</h2>
            <div className="default-message">
              <p>Compare upgrade options to maximize rewards while considering approval likelihood and costs.</p>
              <div className="default-tips">
                <h3><i className="fa-solid fa-lightbulb"></i> Upgrade Optimization Tips:</h3>
                <ul>
                  <li>Product changes avoid hard inquiries but may have limited upgrade options</li>
                  <li>Consider your spending patterns when choosing between cashback and travel cards</li>
                  <li>Calculate the break-even point for annual fees based on your expected rewards</li>
                  <li>Wait 6+ months between card applications or upgrades</li>
                  <li>Maintain low utilization and perfect payment history for best upgrade offers</li>
                  <li>Consider the long-term value beyond just the first-year benefits</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Upgrade Analysis Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
                    <button className="btn-secondary" onClick={printResults}>
                      <i className="fa-solid fa-print"></i> Print
                    </button>
                    <button className="btn-secondary" onClick={generatePDF}>
                      <i className="fa-regular fa-file-lines"></i> Save as PDF
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Annual Net Benefit</h3>
                  <div className={`summary-value ${results.annualNetBenefit > 0 ? "savings" : "warning"}`}>
                    ${results.annualNetBenefit}
                  </div>
                  <div className="summary-note">After fees</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-chart-line"></i></div>
                  <h3>Approval Likelihood</h3>
                  <div className={`summary-value ${
                    results.approvalLikelihood.includes('High') ? "savings" :
                    results.approvalLikelihood.includes('Good') ? "" : "warning"
                  }`}>
                    {results.approvalLikelihood}
                  </div>
                  <div className="summary-note">Score: {results.approvalScore}/100</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-calendar-days"></i></div>
                  <h3>Break-even Point</h3>
                  <div className="summary-value">
                    {results.breakEvenMonths > 0 ? `${results.breakEvenMonths} months` : 'Immediate'}
                  </div>
                  <div className="summary-note">Time to recoup fees</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-credit-card"></i></div>
                  <h3>Estimated New Limit</h3>
                  <div className="summary-value">${results.estimatedNewLimit}</div>
                  <div className="summary-note">
                    {((parseFloat(results.estimatedNewLimit) - parseFloat(currentCard.limit)) / parseFloat(currentCard.limit) * 100).toFixed(0)}% increase
                  </div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Rewards Comparison</h3>
                    {rewardsComparisonData && <Bar data={rewardsComparisonData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Rewards Analysis:</strong> Compare monthly rewards value between your current card and the upgrade option.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Benefits Breakdown</h3>
                    {benefitsBreakdownData && <Bar data={benefitsBreakdownData} options={chartOptions} />}
                    <div className="chart-description">
                      <p><strong>Value Components:</strong> See how different benefits contribute to the total upgrade value.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Spending Distribution</h3>
                    <Doughnut data={spendingDistributionData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Spending Analysis:</strong> Understand how your spending aligns with the upgrade card's bonus categories.</p>
                    </div>
                  </div>

                  {scenarioComparison && (
                    <div className="chart-card">
                      <h3>Upgrade Options Comparison</h3>
                      <Bar data={scenarioComparisonData} options={scenarioChartOptions} />
                      <div className="chart-description">
                        <p><strong>Multi-Option Analysis:</strong> Compare all upgrade options to find the best fit for your profile.</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    <div className="result-card">
                      <h3>Financial Analysis - {results.upgradeOption}</h3>
                      <div className="result-stats">
                        <div className="stat">
                          <label>Current Monthly Rewards</label>
                          <span className="stat-value">${results.currentRewards}</span>
                        </div>
                        <div className="stat">
                          <label>Upgrade Monthly Rewards</label>
                          <span className="stat-value">${results.upgradeRewards}</span>
                        </div>
                        <div className="stat">
                          <label>Monthly Rewards Increase</label>
                          <span className="stat-value savings">+${results.monthlyRewardsIncrease}</span>
                        </div>
                        <div className="stat">
                          <label>Annual Net Benefit</label>
                          <span className={`stat-value ${results.annualNetBenefit > 0 ? "savings" : "warning"}`}>
                            ${results.annualNetBenefit}
                          </span>
                        </div>
                        <div className="stat">
                          <label>First Year Total Value</label>
                          <span className="stat-value savings">${results.firstYearValue}</span>
                        </div>
                        <div className="stat">
                          <label>Sign-up Bonus Value</label>
                          <span className="stat-value">${results.signupBonusValue}</span>
                        </div>
                        <div className="stat">
                          <label>Foreign Fee Savings</label>
                          <span className="stat-value">${results.foreignFeeSavings}/month</span>
                        </div>
                        <div className="stat">
                          <label>Break-even Period</label>
                          <span className="stat-value">
                            {results.breakEvenMonths > 0 ? `${results.breakEvenMonths} months` : 'Immediate'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="result-card">
                      <h3>Approval & Credit Impact</h3>
                      <div className="result-stats">
                        <div className="stat">
                          <label>Approval Likelihood</label>
                          <span className={`stat-value ${
                            results.approvalLikelihood.includes('High') ? "savings" :
                            results.approvalLikelihood.includes('Good') ? "" : "warning"
                          }`}>
                            {results.approvalLikelihood} ({results.approvalScore}/100)
                          </span>
                        </div>
                        <div className="stat">
                          <label>Current Credit Limit</label>
                          <span className="stat-value">${currentCard.limit}</span>
                        </div>
                        <div className="stat">
                          <label>Estimated New Limit</label>
                          <span className="stat-value">${results.estimatedNewLimit}</span>
                        </div>
                        <div className="stat">
                          <label>Limit Increase</label>
                          <span className="stat-value savings">
                            +{((parseFloat(results.estimatedNewLimit) - parseFloat(currentCard.limit)) / parseFloat(currentCard.limit) * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="stat">
                          <label>Current Utilization</label>
                          <span className="stat-value">{financialProfile.utilization}%</span>
                        </div>
                        <div className="stat">
                          <label>Utilization After Upgrade</label>
                          <span className="stat-value">{results.utilizationAfterUpgrade}%</span>
                        </div>
                        <div className="stat">
                          <label>Upgrade Path</label>
                          <span className="stat-value">
                            {upgradeOptions[selectedUpgrade].upgradePath === "product_change" ? 
                             "Product Change (No hard inquiry)" : "New Application (Hard inquiry)"}
                          </span>
                        </div>
                        <div className="stat">
                          <label>Minimum Credit Score Required</label>
                          <span className={`stat-value ${
                            parseInt(financialProfile.creditScore) >= parseInt(upgradeOptions[selectedUpgrade].minScore) ? "savings" : "warning"
                          }`}>
                            {upgradeOptions[selectedUpgrade].minScore} (Yours: {financialProfile.creditScore})
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Upgrade Options Comparison */}
                  {scenarioComparison && (
                    <div className="scenario-comparison">
                      <h3>All Upgrade Options Comparison</h3>
                      <div className="scenario-grid">
                        {scenarioComparison.map((scenario, index) => (
                          <div key={index} className="scenario-card">
                            <h4>{scenario.name}</h4>
                            <div className="scenario-stats">
                              <div className="stat">
                                <label>Annual Net Benefit</label>
                                <span className={scenario.annualNetBenefit > 0 ? "savings" : "warning"}>
                                  ${scenario.annualNetBenefit.toFixed(2)}
                                </span>
                              </div>
                              <div className="stat">
                                <label>First Year Value</label>
                                <span className="savings">${scenario.firstYearValue.toFixed(2)}</span>
                              </div>
                              <div className="stat">
                                <label>Approval Score</label>
                                <span className={
                                  scenario.approvalScore >= 70 ? "savings" :
                                  scenario.approvalScore >= 55 ? "" : "warning"
                                }>
                                  {scenario.approvalScore}/100
                                </span>
                              </div>
                              <div className="stat">
                                <label>Break-even Months</label>
                                <span>{scenario.breakEvenMonths}</span>
                              </div>
                              <div className="stat">
                                <label>Annual Fee</label>
                                <span>${scenario.annualFee.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actionable Recommendations */}
                  <div className="default-results">
                    <h3>Action Plan</h3>
                    <div className="recommendations-list">
                      {results.recommendations.map((rec, i) => (
                        <div key={i} className={`default-tips ${rec.type}`}>
                          <i className={`fa-solid ${
                            rec.type === 'critical' ? 'fa-exclamation-triangle' :
                            rec.type === 'warning' ? 'fa-exclamation-circle' :
                            rec.type === 'success' ? 'fa-check-circle' : 'fa-info-circle'
                          }`}></i>
                          <span>{rec.text}</span>
                        </div>
                      ))}
                      {results.annualNetBenefit > 0 && (
                        <div className="default-tips">
                          <i className="fa-solid fa-check-circle"></i>
                          <span>This upgrade provides positive net value - consider proceeding with the application</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your current card details, financial profile, and upgrade options to analyze upgrade opportunities.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default CreditCardUpgradeAnalyzer;